import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
/**
 * `GET /api/me/payment-methods` — the cards Stripe holds for the caller.
 *
 * Mirrors `PaymentMethod` on the backend. Deliberately narrow: `brand`, `last4`,
 * expiry and an optional cardholder name — enough to render a row and nothing
 * else. The card itself is managed in Stripe's portal, so this app never edits
 * one and the shape does not need the fields an editor would.
 *
 * **`items: []` is a valid answer, not an error.** A member who has never had a
 * Stripe customer id on file (no subscription, no administered org that ever
 * reached Stripe) reads an empty list — the page's empty state is what belongs
 * on screen, with the portal button disabled and explained.
 */
export const PaymentMethod = z.object({
    id: z.string(),
    /** `visa`, `mastercard`, `amex`, `discover`, or `other`. Lower-case. */
    brand: z.string(),
    last4: z.string(),
    expMonth: z.number().int().min(1).max(12),
    expYear: z.number().int(),
    cardholderName: z.string().nullable(),
    isDefault: z.boolean(),
});
export const PaymentMethodsResponse = z.object({
    items: z.array(PaymentMethod),
});
/**
 * Fetch the caller's payment methods.
 *
 * Cached briefly: the page mounts on a member action rather than in a
 * sidebar, and a card added in the portal appears here on the next fetch —
 * the mutation below invalidates on success.
 */
export function usePaymentMethods(http) {
    return useQuery({
        queryKey: ['me', 'payment-methods'],
        queryFn: () => http.get('/me/payment-methods', { schema: PaymentMethodsResponse }),
        staleTime: 30_000,
    });
}
/**
 * `POST /api/me/billing-portal` — a Stripe customer-portal URL.
 *
 * Mirrors `BillingPortalSession` on the backend. This is the deliverable of
 * the payment-method screen: three buttons on the page (Manage, Add, Manage in
 * Stripe) all end here and redirect the browser to `url`.
 *
 * The API throws `STRIPE_CUSTOMER_NOT_FOUND` (404) when the caller holds
 * no customer id at all — the button is then disabled with an explanation
 * rather than clicked into a refusal.
 */
export const BillingPortalSession = z.object({
    url: z.string().url(),
    expiresAt: z.string(),
});
/**
 * Open the Stripe customer portal for this member.
 *
 * Idempotent by the http client, which sends an `Idempotency-Key` on every
 * POST — a portal session is a real create at Stripe and a double-click must
 * not become two rows in the account's history.
 */
export function useOpenBillingPortal(http) {
    return useMutation({
        mutationFn: () => http.post('/me/billing-portal', {}, { schema: BillingPortalSession }),
    });
}
//# sourceMappingURL=payment-methods.js.map